<template>
  <fin-data-loader :resolver="loadData" ref="datafetcher" overlay fullpage>
    <template #content>
      <Portal to="breadcrumb_companyDetail" v-if="instrument">
        {{ instrument.name }}
      </Portal>
      <div class="fin-company-page">

        <template v-if="$route.name !== 'addCompanyPageInstrumentToPortfolio'">
          <fin-instrument-detail-header ref="instrumentPage"
                                        :actions="actions"
                                        :daily-data="dailyData"
                                        :instrument="instrument"
                                        :key-figures="keyFigures"
                                        :no-escape-link="noEscapeLink"
                                        :sub-line-values="subLineValues"
                                        :tabs="tabs"
                                        current-value>
            <template #tab0>
              <!--TODO maybe better handling, with named observables-->
              <fin-company-page-overview :news-articles='newsArticles'
                                         :instrument="instrument"
                                         :no-escape-link="noEscapeLink"
                                         :realtime-subscription="subscriptionManager.observables[0]"
                                         @allNews="switchToNewsTab()"/>

            </template>

            <template #tab1>
              <fin-instrument-performance-page :instrument="instrument"/>
            </template>

            <template #tab2>
              <fin-company-page-profile :id="id"/>
            </template>

            <template #tab3>
              <fin-company-page-key-figures :id="id"/>
            </template>

            <template #tab4>
              <fin-company-page-news-tab :instrument-id="id"/>
            </template>
          </fin-instrument-detail-header>
        </template>
        <router-view :instruments="[instrument]" v-else/>
      </div>
    </template>
  </fin-data-loader>
</template>

<script>
import FinCompanyPageProfile from '@/components/pages/finCompanyPage/Profile'
import {finMasterDataAPI, finShareAPI} from '@/js/finAPI'
import FinCompanyPageOverview from '@/components/pages/finCompanyPage/Overview'
import FinCompanyPageNewsTab from "@/components/pages/finCompanyPage/NewsTab"
import FinInstrumentDetailHeader from "@/components/pages/instrumentPages/instrumentDetailPage"
import {TablePresets} from "@/components/common/DataTable/DefaultHeaders"
import {companyDetailsSubscription} from "@/js/finSubscriptions"
import SubscriptionHandler from "@/mixins/SubscriptionHandler"
import FinDataLoader from "@/components/common/DataLoader"
import FinInstrumentPerformancePage from "@/components/pages/instrumentPages/instrumentPerformancePage"
import FinCompanyPageKeyFigures from "@/components/pages/finCompanyPage/Keyfigures"
import {PRICEMODEL_FEATURE_IDS, PRICEMODEL_FEATURE_RESTRICTIONS} from "@/js/finPriceModel"
import PriceModel from "@/mixins/PriceModel"
import {EventBus} from "@/plugins/fincraft"

export default {
  name: 'fin-company-page',
  mixins: [SubscriptionHandler, PriceModel],
  components: {
    FinCompanyPageKeyFigures,
    FinInstrumentPerformancePage,
    FinDataLoader,
    FinInstrumentDetailHeader,
    FinCompanyPageNewsTab,
    FinCompanyPageOverview,
    FinCompanyPageProfile,
  },
  props: {
    /** The company id, set via url param / router */
    id: {
      type: String
    },
    displayColor: {
      type: String,
      default: '$finColor.primaryDark'
    },
    /** If the company page is opened from the onboarding, then no link should allow the user to quit the onboarding pages */
    noEscapeLink: {
      type: Boolean,
      default: false
    },
  },
  data() {
    return {
      subLineValues: TablePresets.createShareDetailPageSubtitle(),
      keyFigures: TablePresets.createShareDetailPageKeyFigures(),
      dailyData: TablePresets.createDetailPageDailyDevelopment(),
      hideNews: process.env.VUE_APP_HIDE_NEWS_ALL === 'true',
      newsArticles: [],
      instrument: undefined,
      actions: [
        {
          enabled: () => this.isPriceModelFeatureEnabled(PRICEMODEL_FEATURE_IDS.SHARE_RATING.id),
          name: 'Bewerte eine Aktie',
          icon: 'icon-tools',
          click: (inst) => {
            if (this.isPriceModelFeatureEnabled(PRICEMODEL_FEATURE_IDS.SHARE_RATING.id)) {
              this.$router.push({name: 'finToolsRateSharesRatingCreate', params: {sid: inst.id}})
            } else {
              EventBus.$emit('showSubscriptionPopup', PRICEMODEL_FEATURE_IDS.SHARE_RATING.id, PRICEMODEL_FEATURE_RESTRICTIONS.UNAVAILABLE)
            }
          },
        }
      ]
    }
  },
  computed: {
    tabs() {
      const keyFiguresAllowed = this.isPriceModelFeatureEnabled(PRICEMODEL_FEATURE_IDS.KEY_FIGURES.id)
      const result = [{
        name: 'Übersicht',
        id: 'OVERVIEW'
      }, {
        name: 'Performance',
        id: 'PERFORMANCE',
      }, {
        name: 'Profil',
        id: 'PROFIL',
      }, {
        name: 'Kennzahlen',
        id: 'KEY_FIGURES',
        allowed: keyFiguresAllowed
      }]
      if (!this.hideNews && !this.noEscapeLink) {
        result.push({
          name: 'Nachrichten',
          id: 'NEWS',
        })
      }
      return result
    }
  },
  methods: {
    async loadData() {
      this.unsubscribeChanges()
      if (!this.hideNews && !this.noEscapeLink) {
        await this.loadNews()
      }
      await this.loadCompany()
    },
    async loadCompany() {
      this.instrument = await finShareAPI.getShareCompany(this.id)

      if (this.instrument.latestPrice.quality === 'RT') {
        const {priceChange, priceChangePercentage} = this.calcPriceChange(this.instrument.latestPrice.price)
        this.instrument.latestPrice.priceChange = priceChange
        this.instrument.latestPrice.priceChangePercentage = priceChangePercentage
      }
      this.subscribeToChanges(
        this.instrument,
        companyDetailsSubscription,
        (company) => {
          return {id: company.isin}
        },
        this.handleSubscription
      )
    },
    async loadNews() {
      let result = await finMasterDataAPI.searchNewsTeasers(4, undefined, {relatedShares: {eq: this.id}})
      try {
        this.newsArticles = result.items
      } catch (e) {
        console.error("Could not load searchNewsTeasers")
        this.newsArticles = []
      }
    },
    switchToNewsTab() {
      const indexTabNews = this.tabs.findIndex(item => item.name === 'Nachrichten')
      this.$refs.instrumentPage.selectTab(indexTabNews)
    },
    calcPriceChange(newPrice) {
      let priceChange = newPrice - this.instrument.close
      let priceChangePercentage = ((newPrice - this.instrument.close) / this.instrument.close) * 100

      return {
        priceChange: priceChange,
        priceChangePercentage: priceChangePercentage
      }
    },
    handleSubscription(data) {
      const {
        id,
        price,
        timestamp,
        high,
        low,
        exchangeName
      } = data.data.onCreateRealTimePrice
      if (id === this.instrument.isin) {

        const {priceChange, priceChangePercentage} = this.calcPriceChange(price)

        this.instrument.latestPrice = {
          price: price,
          timestamp: timestamp,
          exchangeName: exchangeName,
          high: high,
          low: low,
          open: this.instrument.latestPrice.open,
          priceChange: priceChange,
          priceChangePercentage: priceChangePercentage
        }
      }
    },
  },
  watch: {
    id() {
      // url param id changes e.g. because of search -> reload page data
      this.$refs.datafetcher._fetchData()
    },
  },
}
</script>

<style lang="scss" scoped>

</style>
